import type { Dispatch, PropsWithChildren, Reducer } from 'react';
import { createContext, useContext, useReducer } from 'react';
import type { DesignBoardCollaborator } from '@modules/design-board/validation/types';
import invariant from 'tiny-invariant';
import { useLoaderData } from '@remix-run/react';
import type { loader } from '~/routes/_app.member.design-boards.$boardUrlSegment._index';
import type {
    InviteStatus,
    Visibility,
} from '@modules/design-board/component/manage/collaborator-overview/CollaboratorOverview.types';
import { ShareDesignBoardDialog } from '../../share-design-baord/ShareDesignBoardDialog';

interface DesignBoardCollaboratorsState {
    boardId: number;
    boardName: string;
    visibility: Visibility;
    owner: DesignBoardCollaborator;
    removeCollaboratorConfirmationVisibility: Visibility;
    removingCollaborator?: {
        email: string;
        inviteStatus: InviteStatus;
        confirmed: boolean;
    };
    isShareBoardModalOpen: boolean;
}

interface IDesignBoardCollaboratorsContext {
    state: DesignBoardCollaboratorsState;
    dispatch: Dispatch<Action>;
}

type Action =
    | {
          type: 'modal-visibility';
          data: {
              visibility: Visibility;
          };
      }
    | {
          type: 'show-remove-collaborator-confirmation';
          data: { email: string; inviteStatus: InviteStatus };
      }
    | {
          type: 'hide-remove-collaborator-confirmation';
      }
    | {
          type: 'confirm-collaborator-removal';
          data: { email: string; inviteStatus: InviteStatus };
      }
    | {
          type: 'openShareBoardModal';
      }
    | {
          type: 'closeShareBoardModal';
      };

const DesignBoardCollaboratorsContext =
    createContext<IDesignBoardCollaboratorsContext | null>(null);

const reducer: Reducer<DesignBoardCollaboratorsState, Action> = (
    state: DesignBoardCollaboratorsState,
    action: Action
) => {
    switch (action.type) {
        case 'modal-visibility':
            return { ...state, visibility: action.data.visibility };
        case 'show-remove-collaborator-confirmation':
            return {
                ...state,
                removeCollaboratorConfirmationVisibility: 'visible',
                removingCollaborator: {
                    email: action.data.email,
                    inviteStatus: action.data.inviteStatus,
                    confirmed: false,
                },
            };
        case 'confirm-collaborator-removal':
            return {
                ...state,
                removeCollaboratorConfirmationVisibility: 'visible',
                removingCollaborator: {
                    email: action.data.email,
                    inviteStatus: action.data.inviteStatus,
                    confirmed: true,
                },
            };
        case 'hide-remove-collaborator-confirmation':
            return {
                ...state,
                removeCollaboratorConfirmationVisibility: 'hidden',
            };
        case 'openShareBoardModal':
            return {
                ...state,
                isShareBoardModalOpen: true,
            };
        case 'closeShareBoardModal':
            return {
                ...state,
                isShareBoardModalOpen: false,
            };
        default:
            return state;
    }
};

interface ContextProviderProps extends PropsWithChildren {
    boardId: number;
    boardName: string;
}

export const DesignBoardCollaboratorsContextProvider = ({
    children,
    boardId,
    boardName,
}: ContextProviderProps) => {
    const { collaborators } = useLoaderData<typeof loader>();
    const owner = collaborators.find((c) => c.membership === 'owners');

    invariant(
        owner,
        'DesignBoardCollaboratorsContextProvider must have an owner'
    );

    const [state, dispatch] = useReducer(reducer, {
        owner,
        visibility: 'hidden',
        removeCollaboratorConfirmationVisibility: 'hidden',
        boardId,
        boardName,
        isShareBoardModalOpen: false,
    });

    return (
        <DesignBoardCollaboratorsContext.Provider value={{ state, dispatch }}>
            <ShareDesignBoardDialog open={state.isShareBoardModalOpen} />
            {children}
        </DesignBoardCollaboratorsContext.Provider>
    );
};

export const useDesignBoardCollaboratorsContext = () => {
    const ctx = useContext(DesignBoardCollaboratorsContext);

    if (!ctx) {
        throw new Error(
            "can't use DesignBoardCollaboratorsContext outside of DesignBoardCollaboratorsContextProvider"
        );
    }

    return ctx;
};
